import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

export function ReceivePODialog({ po, onClose, onSaved }) {
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const waiting = (po?.items || []).filter((it) => it.boxes > it.received_boxes);

  useEffect(() => {
    if (po) setQty(Object.fromEntries(po.items.map((it) => [it.product_id, it.boxes - it.received_boxes])));
  }, [po]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const items = waiting.map((it) => ({ product_id: it.product_id, boxes: Number(qty[it.product_id]) || 0 }));
      const { data } = await api.post(`/admin/purchase-orders/${po.id}/receive`, { items });
      const vials = data.receipts[data.receipts.length - 1].items.reduce((s, x) => s + x.vials, 0);
      toast.success(`${vials} vials added to stock`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="po-receive-dialog">
        <DialogHeader><DialogTitle>Receive {po?.po_number}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground">Enter how many boxes arrived. Stock goes up by boxes × vials per box. Anything not received stays as waiting.</p>
          {waiting.map((it) => {
            const left = it.boxes - it.received_boxes;
            const n = Number(qty[it.product_id]) || 0;
            return (
              <div key={it.product_id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`po-receive-line-${it.product_id}`}>
                <div>
                  <p className="text-sm font-medium">{it.name} <span className="font-mono-data text-xs text-muted-foreground">{it.vial_size}</span></p>
                  <p className="font-mono-data text-xs text-muted-foreground">{left} of {it.boxes} box(es) waiting · +{n * it.box_size} vials</p>
                </div>
                <Input type="number" min="0" max={left} value={qty[it.product_id] ?? ""} onChange={(e) => setQty({ ...qty, [it.product_id]: e.target.value })}
                  className="w-20 font-mono-data" aria-label="Boxes arrived" data-testid={`po-receive-boxes-${it.product_id}`} />
              </div>
            );
          })}
          <Button type="submit" className="w-full" disabled={busy} data-testid="po-receive-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add to stock</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
